import { makeStyles, Typography } from '@material-ui/core';
import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { useTheme } from '@material-ui/core/styles';
import SignaturePad from 'signature_pad';

const useStyles = makeStyles(theme => ({
  root: {
    position: 'relative',
    width: '100%',
    overflow: 'hidden',
    borderRadius: '24px',
    border: `4px dashed ${theme.palette.divider}`,
    transition: 'background-color .2s ease'
  },

  canvas: {
    width: '100%',
    height: '100%',
    display: 'block'
  },

  placeholder: {
    position: 'absolute',
    left: 0,
    right: 0,
    bottom: '24px',
    textAlign: 'center',
    pointerEvents: 'none'
  }
}));

const Assinatura = forwardRef(
  (
    {
      altura = 320,
      placeholder = 'Use o mouse ou toque na tela para assinar',
      mostrarPlaceholder = true,
      somenteLeitura = false,
      onStatusChange
    },
    ref
  ) => {
    const theme = useTheme();

    const configuracaoPad = React.useMemo(
      () => ({
        penColor: theme.palette.text.primary,
        minWidth: 0.5,
        maxWidth: 2.5
      }),
      [theme.palette.text.primary]
    );

    const [alterado, setAlterado] = useState(false);
    const [vazio, setVazio] = useState(true);
    const [habilitado, setHabilitado] = useState(!somenteLeitura);

    const classes = useStyles();

    const canvasRef = useRef(null);
    const assinaturaRef = useRef(null);
    const cleanupRef = useRef(null);
    const animacaoRef = useRef(null);
    const animandoRef = useRef(false);
    const cancelouAnimacaoRef = useRef(false);

    const calcularDuracaoAssinatura = dados => {
      let inicio = null;
      let fim = null;
      dados.forEach(stroke => {
        if (!stroke.points?.length) {
          return;
        }
        const primeiro = stroke.points[0].time;
        const ultimo = stroke.points[stroke.points.length - 1].time;
        if (inicio === null || primeiro < inicio) {
          inicio = primeiro;
        }
        if (fim === null || ultimo > fim) {
          fim = ultimo;
        }
      });
      if (inicio === null || fim === null) {
        return 0;
      }
      return fim - inicio;
    };

    const criarEventosAnimacao = dados => {
      const eventos = [];
      dados.forEach((stroke, strokeIndex) => {
        stroke.points.forEach((point, pointIndex) => {
          eventos.push({
            strokeIndex,
            pointIndex,
            point,
            time: point.time
          });
        });
      });
      return eventos;
    };

    const reconstruirDesenho = (dados, eventos, quantidadeEventos) => {
      const desenho = dados.map(stroke => ({
        ...stroke,
        points: []
      }));
      for (let i = 0; i < quantidadeEventos; i++) {
        const evento = eventos[i];
        desenho[evento.strokeIndex].points.push(evento.point);
      }
      return desenho.filter(stroke => stroke.points.length > 0);
    };

    const renderizarDesenho = desenho => {
      assinaturaRef.current.clear();
      assinaturaRef.current.fromData(desenho);
    };

    const animarAssinatura = async (dados, duracaoDesejadaMs = 1500, reverse = false) => {
      if (!assinaturaRef.current) {
        return;
      }
      if (typeof dados === 'string') {
        dados = JSON.parse(dados);
      }
      if (!Array.isArray(dados) || !dados.length) {
        return;
      }
      animandoRef.current = true;
      cancelouAnimacaoRef.current = false;
      if (animacaoRef.current) {
        cancelAnimationFrame(animacaoRef.current);
      }
      if (!reverse) {
        assinaturaRef.current.clear();
      }
      desabilitar();
      const eventos = criarEventosAnimacao(dados);
      const totalEventos = eventos.length;
      const duracaoOriginal = calcularDuracaoAssinatura(dados);
      const fatorTempo = duracaoOriginal > 100 ? duracaoDesejadaMs / duracaoOriginal : 1;
      const intervaloPontos = totalEventos > 500 ? 5 : 2;
      const inicioOriginal = eventos[0].time;
      const fimOriginal = eventos[totalEventos - 1].time;
      const inicioAnimacao = performance.now();
      if (reverse) {
        renderizarDesenho(dados);
      }
      for (let frame = 0; frame <= totalEventos; frame++) {
        if (cancelouAnimacaoRef.current) {
          animandoRef.current = false;
          if (!somenteLeitura) {
            habilitar();
          }
          return;
        }
        const quantidadeEventos = reverse ? totalEventos - frame : frame;
        const desenho = reconstruirDesenho(dados, eventos, quantidadeEventos);
        if (frame % intervaloPontos === 0 || frame === totalEventos) {
          renderizarDesenho(desenho);
        }
        if (frame === totalEventos) {
          break;
        }
        const evento = reverse ? eventos[quantidadeEventos - 1] : eventos[quantidadeEventos];
        if (!evento) {
          continue;
        }
        const tempoOriginal = reverse ? fimOriginal - evento.time : evento.time - inicioOriginal;
        const tempoAlvo = tempoOriginal * fatorTempo;
        const tempoAnimacao = performance.now() - inicioAnimacao;
        const espera = tempoAlvo - tempoAnimacao;
        if (espera > 0) {
          await new Promise(resolve => setTimeout(resolve, espera));
        }
      }
      assinaturaRef.current.clear();
      if (!reverse) {
        assinaturaRef.current.fromData(dados);
      }
      animandoRef.current = false;
      if (!somenteLeitura) {
        habilitar();
      }
      atualizarEstado({
        alteradoAtual: false
      });
    };

    const alterarPenColorDadosAssinatura = (dados, penColor) => {
      if (!dados) {
        return dados;
      }
      const eraString = typeof dados === 'string';
      const assinatura = eraString ? JSON.parse(dados) : dados;
      const resultado = assinatura.map(pointGroup => ({
        ...pointGroup,
        penColor
      }));
      return eraString ? JSON.stringify(resultado) : resultado;
    };

    const redimensionar = () => {
      if (!assinaturaRef.current) return;
      const canvas = canvasRef.current;
      if (!canvas) return;
      const ratio = Math.max(window.devicePixelRatio || 1, 1);
      const novaLargura = canvas.offsetWidth * ratio;
      const novaAltura = canvas.offsetHeight * ratio;
      if (canvas.width === novaLargura && canvas.height === novaAltura) return;
      const dados = assinaturaRef.current.toData();
      canvas.width = novaLargura;
      canvas.height = novaAltura;
      const context = canvas.getContext('2d');
      context.scale(ratio, ratio);
      if (dados.length > 0) {
        desenhar(dados);
      }
    };

    const desenhar = (dados, atualizar = true) => {
      assinaturaRef.current.clear();
      if (Array.isArray(dados) && dados.length > 0) {
        assinaturaRef.current.fromData(dados);
      }
      if (atualizar) {
        atualizarEstado();
      }
    };

    const atualizarEstado = ({ alteradoAtual = alterado } = {}) => {
      if (!assinaturaRef.current) return;
      const vazioAtual = assinaturaRef.current.isEmpty();
      setAlterado(alteradoAtual);
      setVazio(vazioAtual);
      onStatusChange?.({
        alterado: alteradoAtual,
        vazio: vazioAtual,
        animando: animandoRef.current
      });
    };

    const obterLimites = (dados, margem = 5) => {
      let minX = Infinity;
      let minY = Infinity;
      let maxX = -Infinity;
      let maxY = -Infinity;
      dados.forEach(stroke => {
        stroke.points.forEach(point => {
          minX = Math.min(minX, point.x);
          minY = Math.min(minY, point.y);
          maxX = Math.max(maxX, point.x);
          maxY = Math.max(maxY, point.y);
        });
      });
      return {
        minX: minX - margem,
        minY: minY - margem,
        maxX: maxX + margem,
        maxY: maxY + margem
      };
    };

    const criarCanvasVazio = ({
      largura = null,
      altura = null,
      usarEscalaTela = largura == null && altura == null
    } = {}) => {
      const canvas = document.createElement('canvas');
      if (usarEscalaTela) {
        const ratio = window.devicePixelRatio || 1;
        canvas.width = (largura ?? canvasRef.current.offsetWidth) * ratio;
        canvas.height = (altura ?? canvasRef.current.offsetHeight) * ratio;
        canvas.getContext('2d').scale(ratio, ratio);
      } else {
        canvas.width = largura ?? 1;
        canvas.height = altura ?? 1;
      }
      return canvas;
    };

    const criarCanvasAssinatura = (dados = null, corCaneta = null, margem = 5, exportScale = 1) => {
      if (typeof dados === 'string') {
        dados = JSON.parse(dados);
      }
      dados ??= assinaturaRef.current.toData();
      if (!Array.isArray(dados) || dados.length === 0) {
        return criarCanvasVazio();
      }
      const penColorExportacao = corCaneta ?? '#000000';
      const limites = obterLimites(dados, margem);
      const largura = Math.ceil(limites.maxX - limites.minX);
      const altura = Math.ceil(limites.maxY - limites.minY);
      const ratio = (window.devicePixelRatio || 1) * exportScale;
      const canvas = document.createElement('canvas');
      canvas.width = largura * ratio;
      canvas.height = altura * ratio;
      const ctx = canvas.getContext('2d');
      ctx.scale(ratio, ratio);
      const padExportacao = new SignaturePad(canvas, {
        penColor: penColorExportacao,
        minWidth: 0.5 * exportScale,
        maxWidth: 2.5 * exportScale
      });
      const dadosAjustados = dados.map(stroke => ({
        ...stroke,
        penColor: penColorExportacao,
        points: stroke.points.map(point => ({
          ...point,
          x: point.x - limites.minX,
          y: point.y - limites.minY
        }))
      }));
      padExportacao.fromData(dadosAjustados);
      padExportacao.off();
      return canvas;
    };

    const aplicarCorFundoCanvas = (canvasOrigem, corFundo) => {
      if (!corFundo) {
        return canvasOrigem;
      }
      const canvas = document.createElement('canvas');
      canvas.width = canvasOrigem.width;
      canvas.height = canvasOrigem.height;
      const ctx = canvas.getContext('2d');
      ctx.fillStyle = corFundo;
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(canvasOrigem, 0, 0);
      return canvas;
    };

    const normalizarDados = dados => {
      if (dados == null) {
        return assinaturaRef.current.toData();
      }
      if (typeof dados === 'string') {
        return JSON.parse(dados);
      }
      return dados;
    };

    const inicializar = () => {
      if (assinaturaRef.current) return;
      const pad = new SignaturePad(canvasRef.current, configuracaoPad);
      assinaturaRef.current = pad;
      if (somenteLeitura) {
        desabilitar();
      }
      redimensionar();
      const handleEnd = () => {
        atualizarEstado({
          alteradoAtual: true
        });
      };
      pad.addEventListener('endStroke', handleEnd);
      cleanupRef.current = () => {
        cancelouAnimacaoRef.current = true;
        if (animacaoRef.current) {
          cancelAnimationFrame(animacaoRef.current);
        }
        pad.removeEventListener('endStroke', handleEnd);
        pad.off();
        assinaturaRef.current = null;
      };
    };

    const habilitar = () => {
      assinaturaRef.current?.on();
      setHabilitado(true);
    };

    const desabilitar = () => {
      assinaturaRef.current?.off();
      setHabilitado(false);
    };

    useEffect(() => {
      return () => {
        cleanupRef.current?.();
      };
    }, []);

    useEffect(() => {
      if (!assinaturaRef.current) {
        return;
      }
      if (somenteLeitura) {
        desabilitar();
      } else {
        habilitar();
      }
    }, [somenteLeitura]);

    useEffect(() => {
      window.addEventListener('resize', redimensionar);
      return () => {
        window.removeEventListener('resize', redimensionar);
      };
    }, []);

    useImperativeHandle(ref, () => ({
      estaVazio() {
        return assinaturaRef.current?.isEmpty() ?? true;
      },

      obterDados() {
        return assinaturaRef.current?.toData() ?? [];
      },

      obterImagem({ dados = null, tipo = 'image/png', qualidade = 1, corFundo = null, corCaneta = '#000000' } = {}) {
        dados = normalizarDados(dados);
        let canvas = criarCanvasAssinatura(dados, corCaneta);
        canvas = aplicarCorFundoCanvas(canvas, corFundo);
        return canvas.toDataURL(tipo, qualidade);
      },

      limpar() {
        if (animandoRef.current || somenteLeitura || !habilitado) {
          return;
        }
        desenhar(null);
        atualizarEstado({
          alteradoAtual: true
        });
      },

      definirDados(dados) {
        if (!assinaturaRef.current) {
          return;
        }
        const dadosNormalizados = normalizarDados(dados);
        requestAnimationFrame(() => {
          desenhar(dadosNormalizados);
          atualizarEstado({
            alteradoAtual: false
          });
        });
      },

      animarAssinatura(dados, duracaoDesejadaMs = 1500, reverse = false) {
        if (!assinaturaRef.current) {
          return;
        }
        const dadosNormalizados = normalizarDados(dados);
        requestAnimationFrame(() => {
          animarAssinatura(dadosNormalizados, duracaoDesejadaMs, reverse);
          atualizarEstado({
            alteradoAtual: false
          });
        });
      },

      estaAnimando() {
        return animandoRef.current;
      },

      desfazer() {
        if (animandoRef.current || somenteLeitura) {
          return;
        }
        const dados = assinaturaRef.current.toData();
        dados.pop();
        desenhar(dados);
        atualizarEstado({
          alteradoAtual: true
        });
      },

      inicializar() {
        inicializar();
      },

      redimensionar() {
        redimensionar();
      },

      habilitar() {
        habilitar();
      },

      desabilitar() {
        desabilitar();
      },

      alterarPenColorDadosAssinatura(dados, penColor = '#000000') {
        return alterarPenColorDadosAssinatura(dados, penColor);
      },

      definirCor(cor) {
        assinaturaRef.current.penColor = cor;
      },

      definirEspessura(min, max) {
        assinaturaRef.current.minWidth = min;
        assinaturaRef.current.maxWidth = max;
      },

      marcarComoSalvo() {
        atualizarEstado({
          alteradoAtual: false
        });
      }
    }));

    return (
      <div
        className={classes.root}
        style={{
          height: altura,
          backgroundColor: habilitado ? theme.palette.background.paper : theme.palette.action.disabledBackground,
          opacity: habilitado ? 1 : 0.85,
          transition: 'all .2s ease'
        }}
      >
        <canvas
          ref={canvasRef}
          style={{
            width: '100%',
            height: altura,
            display: 'block',
            background: 'transparent'
          }}
        />
        {mostrarPlaceholder && vazio && (
          <Typography className={classes.placeholder} variant="body2" color="textSecondary">
            {placeholder}
          </Typography>
        )}
      </div>
    );
  }
);

export default Assinatura;
